import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, CheckCircle } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import axios from "axios";
import SEO from "../../components/g4/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const ABSTRACT_BG =
    "https://static.prod-images.emergentagent.com/jobs/14d3d746-4feb-408b-902b-6715506dd42e/images/8471a695aea210df75eace957c4bd6e70e4f2582fee6e7928b1f2e7c68baf061.png";

const PROJECT_TYPES = [
    "Residential — New Build",
    "Residential — Renovation",
    "Commercial — Office",
    "Commercial — Retail",
    "Hospitality",
    "Cultural / Institutional",
    "Mixed-Use Development",
    "Interior Design Only",
    "Urban Planning / Master Plan",
    "Other",
];
const BUDGET_RANGES = [
    "Under £50,000",
    "£50,000 — £150,000",
    "£150,000 — £500,000",
    "£500,000 — £1,000,000",
    "£1,000,000 — £5,000,000",
    "£5,000,000+",
    "Not yet determined",
];
const TIMELINES = [
    "Urgent — within 3 months",
    "3 — 6 months",
    "6 — 12 months",
    "12 — 24 months",
    "Flexible / No rush",
    "Planning stage only",
];

export default function RequestQuote() {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        budget_range: "",
        timeline: "",
        location: "",
        description: "",
        site_area: "",
        reference_links: "",
    });
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [error, setError] = useState("");

    const update = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.project_type || !form.budget_range || !form.timeline) {
            setError("Please fill in all required fields.");
            return;
        }
        setSending(true);
        setError("");
        try {
            await axios.post(`${API}/quote-request`, form);
            setSent(true);
        } catch (err) {
            setError("Failed to submit. Please try again.");
        }
        setSending(false);
    };

    if (sent) {
        return (
            <div
                data-testid="quote-success-page"
                className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6"
            >
                <div className="max-w-md text-center">
                    <CheckCircle size={48} strokeWidth={1} className="text-[#9A8B7E] mx-auto mb-6" />
                    <h1 className="font-['Outfit'] text-3xl tracking-tight text-[#0A0A0A] mb-3">
                        Enquiry Received
                    </h1>
                    <p className="text-base text-[#525252] leading-relaxed mb-8">
                        Thank you for your interest. We've received your project brief and will be in touch within 24 hours to arrange an initial consultation.
                    </p>
                    <div className="flex gap-4 justify-center">
                        <Link
                            to="/"
                            data-testid="quote-back-home"
                            className="btn-studio px-8 py-3 text-sm tracking-wide font-medium"
                        >
                            Back to Home
                        </Link>
                        <button
                            onClick={() => {
                                setSent(false);
                                setForm({
                                    name: "",
                                    email: "",
                                    phone: "",
                                    project_type: "",
                                    budget_range: "",
                                    timeline: "",
                                    location: "",
                                    description: "",
                                    site_area: "",
                                    reference_links: "",
                                });
                            }}
                            data-testid="quote-submit-another"
                            className="btn-studio-outline px-8 py-3 text-sm tracking-wide font-medium"
                        >
                            Submit Another
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-testid="request-quote-page" className="min-h-screen bg-[#FAFAFA]">
            <SEO
                title="Get in Touch"
                description="Submit your project brief and we'll arrange an initial consultation to discuss your project."
                path="/request-quote"
            />
            <div className="bg-white border-b border-[#E5E5E5]">
                <div className="max-w-5xl mx-auto px-6 md:px-12 flex items-center justify-between h-16">
                    <Link
                        to="/"
                        className="font-['Outfit'] text-lg tracking-tight text-[#0A0A0A] font-light"
                    >
                        Studio <span className="font-semibold" style={{ color: "#650505" }}>G4</span>
                    </Link>
                    <Link
                        to="/"
                        data-testid="quote-back-link"
                        className="flex items-center gap-2 text-sm text-[#525252] hover:text-[#0A0A0A] transition-colors"
                    >
                        <ArrowLeft size={16} /> Home
                    </Link>
                </div>
            </div>

            <div className="max-w-5xl mx-auto px-6 md:px-12 py-16 md:py-24">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-16">
                    <div className="lg:col-span-2">
                        <h1 className="font-['Outfit'] text-3xl md:text-4xl tracking-tight font-normal text-[#0A0A0A] mb-6 leading-tight">
                            Tell Us About Your Project
                        </h1>
                        <p className="text-base leading-relaxed text-[#525252] mb-8">
                            Share the details of your vision and we'll set up an initial consultation to discuss further
                        </p>
                        <div className="relative hidden lg:block">
                            <img
                                src={ABSTRACT_BG}
                                alt=""
                                className="w-full aspect-[3/4] object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#FAFAFA] via-transparent to-transparent" />
                        </div>
                    </div>

                    <div className="lg:col-span-3">
                        <form
                            onSubmit={handleSubmit}
                            data-testid="quote-form"
                            className="bg-white border border-[#E5E5E5] p-8 md:p-10 space-y-6"
                        >
                            <div>
                                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A] mb-4 pb-2 border-b border-[#E5E5E5]">
                                    Your Details
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                            Full Name *
                                        </label>
                                        <Input
                                            data-testid="quote-name-input"
                                            value={form.name}
                                            onChange={(e) => update("name", e.target.value)}
                                            required
                                            className="rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0 h-11"
                                            placeholder="James Wilson"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                            Email *
                                        </label>
                                        <Input
                                            data-testid="quote-email-input"
                                            type="email"
                                            value={form.email}
                                            onChange={(e) => update("email", e.target.value)}
                                            required
                                            className="rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0 h-11"
                                            placeholder="james@example.co.uk"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                            Phone
                                        </label>
                                        <Input
                                            data-testid="quote-phone-input"
                                            value={form.phone}
                                            onChange={(e) => update("phone", e.target.value)}
                                            className="rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0 h-11"
                                            placeholder="+44 7700 900000"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                            Project Location
                                        </label>
                                        <Input
                                            data-testid="quote-location-input"
                                            value={form.location}
                                            onChange={(e) => update("location", e.target.value)}
                                            className="rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0 h-11"
                                            placeholder="e.g., Fulham, London"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div>
                                <p className="text-xs tracking-[0.15em] uppercase font-bold text-[#0A0A0A] mb-4 pb-2 border-b border-[#E5E5E5]">
                                    Project Details
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-5">
                                    <div>
                                        <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                            Project Type *
                                        </label>
                                        <Select
                                            value={form.project_type}
                                            onValueChange={(v) => update("project_type", v)}
                                        >
                                            <SelectTrigger
                                                data-testid="quote-project-type-select"
                                                className="rounded-none border-[#E5E5E5] h-11"
                                            >
                                                <SelectValue placeholder="Select type" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-none">
                                                {PROJECT_TYPES.map((t) => (
                                                    <SelectItem key={t} value={t} className="rounded-none">
                                                        {t}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                            Budget Range *
                                        </label>
                                        <Select
                                            value={form.budget_range}
                                            onValueChange={(v) => update("budget_range", v)}
                                        >
                                            <SelectTrigger
                                                data-testid="quote-budget-select"
                                                className="rounded-none border-[#E5E5E5] h-11"
                                            >
                                                <SelectValue placeholder="Select budget" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-none">
                                                {BUDGET_RANGES.map((b) => (
                                                    <SelectItem key={b} value={b} className="rounded-none">
                                                        {b}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                            Timeline *
                                        </label>
                                        <Select
                                            value={form.timeline}
                                            onValueChange={(v) => update("timeline", v)}
                                        >
                                            <SelectTrigger
                                                data-testid="quote-timeline-select"
                                                className="rounded-none border-[#E5E5E5] h-11"
                                            >
                                                <SelectValue placeholder="Select timeline" />
                                            </SelectTrigger>
                                            <SelectContent className="rounded-none">
                                                {TIMELINES.map((t) => (
                                                    <SelectItem key={t} value={t} className="rounded-none">
                                                        {t}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="mb-5">
                                    <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                        Site / Floor Area (optional)
                                    </label>
                                    <Input
                                        data-testid="quote-area-input"
                                        value={form.site_area}
                                        onChange={(e) => update("site_area", e.target.value)}
                                        className="rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0 h-11"
                                        placeholder="e.g., 230 sqm / 2,500 sqft"
                                    />
                                </div>
                                <div className="mb-5">
                                    <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                        Project Description *
                                    </label>
                                    <Textarea
                                        data-testid="quote-description-input"
                                        value={form.description}
                                        onChange={(e) => update("description", e.target.value)}
                                        required
                                        className="rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0 min-h-[140px] resize-none"
                                        placeholder="Describe your project vision, requirements, and any specific needs..."
                                    />
                                </div>
                                <div>
                                    <label className="text-xs tracking-[0.1em] uppercase font-bold text-[#0A0A0A] mb-1.5 block">
                                        Reference / Inspiration Links (optional)
                                    </label>
                                    <Textarea
                                        data-testid="quote-references-input"
                                        value={form.reference_links}
                                        onChange={(e) => update("reference_links", e.target.value)}
                                        className="rounded-none border-[#E5E5E5] focus:border-[#0A0A0A] focus:ring-0 min-h-[80px] resize-none"
                                        placeholder="Share any Pinterest boards, websites, or image links that reflect your taste..."
                                    />
                                </div>
                            </div>

                            {error && (
                                <p data-testid="quote-error" className="text-sm text-red-600">
                                    {error}
                                </p>
                            )}

                            <button
                                type="submit"
                                data-testid="quote-submit-btn"
                                disabled={sending}
                                className="btn-studio px-10 py-3 text-sm tracking-wide font-medium inline-flex items-center gap-2 disabled:opacity-50"
                            >
                                <Send size={16} /> {sending ? "Submitting..." : "Submit Enquiry"}
                            </button>

                            <p className="text-xs text-[#525252] mt-3">
                                We typically respond within 24 hours. Your information is kept confidential.
                            </p>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}
